/**
 @param stores
 @param methods
 */
import { Chip, IconButton, Stack, Tooltip } from "@mui/material";
import { FiCheckCircle, FiClipboard, FiFileText, FiSlash, FiXCircle } from "react-icons/fi";
import { useTranslation } from "react-i18next";
import moment from "moment";

const iconButtonSx = {
  width: 34,
  height: 34,
  borderRadius: "10px",
  border: "1px solid transparent",
};

const getStatusColor = (status) => {
  if (status === "approved") return "success";
  if (status === "cancelled") return "warning";
  if (status === "rejected") return "error";
  return "info";
};

const AdminWithDrawlTable = (withDrawl = [], methods = {}) => {
  const { handleApprovedWithDrawl, handleRejectWithDrawl, toggleReceiptModal, openDocumentModal } = methods;
  const { t } = useTranslation();

  const columns = [
    { field: "id", headerName: t("fields.Sno"), width: 90, hidden: true },
    { field: "fullName", headerName: t("fields.fullName"), width: 220 },
    { field: "walletId", headerName: t("fields.WalletID"), width: 220 },
    {
      field: "transactionId",
      headerName: t("fields.transactionId"),
      width: 200,
      renderCell: ({ row }) => row.transactionId || "-",
    },
    { field: "amount", headerName: t("fields.amount"), width: 120 },
    { field: "bankName", headerName: t("fields.bankName"), width: 220 },
    {
      field: "accountNumber",
      headerName: t("fields.accountNumber"),
      width: 220,
    },
    { field: "rfcNumber", headerName: t("fields.rfcNumber"), width: 220 },
    { field: "Retention", headerName: t("fields.retention"), width: 120 },
    { field: "PaidOut", headerName: t("fields.paidOut"), width: 120 },
    {
      field: "status",
      headerName: t("fields.status"),
      width: 140,
      renderCell: ({ row }) => (
        <Chip
          label={row.status}
          color={getStatusColor(row.status)}
          size="small"
          sx={{ textTransform: "capitalize", fontWeight: 600 }}
        />
      ),
    },
    {
      field: "createdAt",
      headerName: t("fields.createdAt"),
      minWidth: 210,
    },
    {
      field: "actions",
      headerName: t("fields.actions"),
      width: 220,
      sortable: false,
      renderCell: ({ row }) => {
        const isCancelled = row.status === "cancelled";
        const isApproved = row.status === "approved";
        const isRejected = row.status === "rejected";
        const isLocked = isCancelled || isApproved || isRejected;

        return (
          <Stack direction="row" spacing={0.5} alignItems="center">
            {row.document ? (
              <Tooltip title="View attached document" arrow>
                <IconButton
                  size="small"
                  onClick={() => openDocumentModal(row)}
                  sx={{ ...iconButtonSx, color: "#0f5c67", backgroundColor: "#e1f4f6" }}
                >
                  <FiFileText size={16} />
                </IconButton>
              </Tooltip>
            ) : null}

            {isApproved ? (
              <Tooltip title="Approved" arrow>
                <IconButton
                  size="small"
                  disabled
                  sx={{ ...iconButtonSx, color: "#137333", backgroundColor: "#e6f4ea" }}
                >
                  <FiCheckCircle size={16} />
                </IconButton>
              </Tooltip>
            ) : isCancelled ? (
              <Tooltip title="Cancelled already by user" arrow>
                <IconButton
                  size="small"
                  disabled
                  sx={{ ...iconButtonSx, color: "#8a1f17", backgroundColor: "#fdecea" }}
                >
                  <FiSlash size={16} />
                </IconButton>
              </Tooltip>
            ) : isRejected ? (
              <Tooltip title="Rejected" arrow>
                <IconButton
                  size="small"
                  disabled
                  sx={{ ...iconButtonSx, color: "#b3261e", backgroundColor: "#fdecea" }}
                >
                  <FiXCircle size={16} />
                </IconButton>
              </Tooltip>
            ) : (
              <>
                <Tooltip title="Approve withdrawl" arrow>
                  <IconButton
                    size="small"
                    onClick={() => handleApprovedWithDrawl(row._id, row.amount, row.fullName)}
                    sx={{ ...iconButtonSx, color: "#137333", backgroundColor: "#e6f4ea" }}
                  >
                    <FiCheckCircle size={16} />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Reject withdrawl" arrow>
                  <IconButton
                    size="small"
                    onClick={() => handleRejectWithDrawl(row._id)}
                    disabled={isLocked}
                    sx={{ ...iconButtonSx, color: "#b3261e", backgroundColor: "#fdecea" }}
                  >
                    <FiXCircle size={16} />
                  </IconButton>
                </Tooltip>
              </>
            )}

            {row.file ? (
              <Tooltip title="View receipt" arrow>
                <IconButton
                  onClick={() => toggleReceiptModal(row)}
                  size="small"
                  sx={{ ...iconButtonSx, color: "#5b3cc4", backgroundColor: "#efebff" }}
                >
                  <FiClipboard size={16} />
                </IconButton>
              </Tooltip>
            ) : null}
          </Stack>
        );
      },
    },
  ];

  const rows =
    withDrawl.length === 0
      ? []
      : withDrawl.map((item, index) => ({
          id: index + 1,
          fullName: item?.fullName,
          transactionId: item?.transactionId,
          walletId: item?.walletId,
          amount: item?.amount,
          bankName: item?.bankName,
          accountNumber: item?.accountNo,
          rfcNumber: item?.rfcNo,
          Retention: "10%",
          PaidOut: Number(item?.amount || 0) * 0.9,
          status: item?.status,
          createdAt: moment(item?.createdAt).format("MMMM Do YYYY HH:mm"),
          _id: item?._id,
          file: item?.file,
          document: item?.document,
        }));

  return { columns, rows };
};

export default AdminWithDrawlTable;
